import { MRT_ColumnDef } from 'material-react-table';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { hasPermission, useCheckPermissions, useTableFilter } from '@/shared';
import { useFetchMedidasProductos, useUpdateMedidaProducto } from '@/shared/actions/erp';
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
  SwitchComponente,
} from '@/shared/components/ui';
import { MEDIDASPRODUCTOS } from '@/shared/interfaces/erp/inventario';
import { useUiConfirmModalStore } from '@/store/ui';
import { PermissionsEnum } from '@/auth/interfaces';

const returnUrlMedida = '/inventario/medidas-productos';

export interface MedidasProductosPageInterface {}

const MedidasProductosPage: React.FC<MedidasProductosPageInterface> = () => {
  useCheckPermissions(PermissionsEnum.api_view_unidadmedida);

  const navigate = useNavigate();

  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
  } = useTableFilter();
  const { pageIndex } = pagination;

  ///* fetch data
  const {
    data: medidasProductosPagingRes,
    isLoading,
    isRefetching,
  } = useFetchMedidasProductos({
    page: pageIndex + 1,
    nombre: searchTerm,
  });

  ///* mutations
  const updateMedidasProductosMutation = useUpdateMedidaProducto({navigate, returnUrl:returnUrlMedida});
  ///* global state
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );
  ///* handlers
  const onEdit = (original: MEDIDASPRODUCTOS) => {
    navigate(`${returnUrlMedida}/editar/${original.id_unidad_medida}`);
  };

  ///* columns
  const columns = useMemo<MRT_ColumnDef<MEDIDASPRODUCTOS>[]>(
    () => [
    {
      "accessorKey": "numero_referencia",
      "header": "Numero referencia",
      "size": 300,
    },
      {
        accessorKey: 'nombre',
        header: 'Nombre',
        size: 300,
      },
      {
        accessorKey: 'siglas',
        header: 'siglas',
        size: 300,
      },
      {
        accessorKey: 'estado',
        header: 'Estado',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <SwitchComponente
            estadoSwitch={row.original.estado}
            titulo={row.original.estado ? 'Activo' : 'Inactivo'}
            setEstadoSwitch={iva => {
              if (!hasPermission(PermissionsEnum.api_change_unidadmedida)) return;
              setConfirmDialog({
                isOpen: true,
                title: 'Cambiar Estado',
                subtitle:
                  '¿Está seguro que desea cambiar el estado de este registro ?',
                onConfirm: () => {
                  setConfirmDialogIsOpen(false);
                  updateMedidasProductosMutation.mutate({
                    id: row.original.id_unidad_medida!,
                    data: {
                      ...row.original,
                      estado: iva as boolean,
                    },
                  });
                },
              });
            }}
          />
        ),
      },
    ],
    []
  );

  return (
    <SingleTableBoxScene title="Medida Producto" urlRedirect={`${returnUrlMedida}/crear`} enableRedirect={hasPermission(PermissionsEnum.api_add_unidadmedida)}>
      <CustomSearch onChange={onChangeFilter} value={globalFilter} text="medida  o nombre de la medida" />

      <GeneralTable<MEDIDASPRODUCTOS>
        columns={columns}
        data={medidasProductosPagingRes?.data?.data || []}
        //
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // search
        enableGlobalFilter={false}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={medidasProductosPagingRes?.data?.count}
        // // actions
        enableColumnActions={hasPermission(PermissionsEnum.api_change_unidadmedida)}
        canEdit={hasPermission(PermissionsEnum.api_change_unidadmedida)}
        // crud
        onEdit={onEdit}
        canDelete={false}
      />
    </SingleTableBoxScene>
  );
};

export default MedidasProductosPage;
